import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, ShoppingBag, ArrowLeft, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function Product({ store }) {
  const { slug, pid } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const acc = store.secondary_color || "#3DBB2C";

  useEffect(() => { api.get(`/public/store/${slug}/products/${pid}`).then((r) => { setP(r.data); const cs = [...new Set((r.data.variants || []).map((v) => v.color))].filter(Boolean); if (cs.length) setColor(cs[0]); }).catch(() => setP(false)); }, [slug, pid]);

  const colors = useMemo(() => [...new Set((p?.variants || []).map((v) => v.color))].filter(Boolean), [p]);
  const sizesForColor = useMemo(() => (p?.variants || []).filter((v) => (!color || v.color === color)), [p, color]);
  const selectedVariant = (p?.variants || []).find((v) => v.color === color && v.size === size);

  if (p === false) return <div className="max-w-6xl mx-auto px-5 py-20 text-center text-neutral-500">Produit introuvable</div>;
  if (!p) return <div className="max-w-6xl mx-auto px-5 py-20 text-neutral-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</div>;

  const promo = p.sale_price && p.sale_price < p.price;
  const price = promo ? p.sale_price : p.price;

  const add = () => {
    if (colors.length && !color) return toast.error("Choisissez une couleur");
    if (!size) return toast.error("Choisissez une taille");
    if (selectedVariant && selectedVariant.quantity <= 0) return toast.error("Rupture de stock");
    addItem({ product_id: p.id, name: p.name, color: color || "—", size, qty: 1, price, image: p.images?.[0] });
    toast.success("Ajouté au panier");
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-6 text-sm"><ArrowLeft size={16} /> Retour</button>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="aspect-[3/4] bg-neutral-100 rounded-xl overflow-hidden">{p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" data-testid="product-image" />}</div>
        <div>
          <span className="overline-label text-neutral-400">{p.category}</span>
          <h1 className="font-display text-4xl font-bold text-neutral-900 mt-2">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="mono text-2xl" style={{ color: acc }}>{fmtMoney(price)}</span>
            {promo && <span className="mono text-neutral-400 line-through">{fmtMoney(p.price)}</span>}
          </div>
          <p className="text-neutral-600 mt-4 leading-relaxed">{p.description}</p>

          {colors.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-medium text-neutral-900 mb-2">Couleur</div>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => (
                  <button key={c} onClick={() => { setColor(c); setSize(""); }} data-testid="storefront-variant-color"
                    className={`px-4 py-2 rounded-full text-sm border ${color === c ? "text-white border-transparent" : "border-black/15 text-neutral-700 hover:border-black/40"}`}
                    style={color === c ? { background: acc } : {}}>{c}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <div className="text-sm font-medium text-neutral-900 mb-2">Taille</div>
            <div className="flex flex-wrap gap-2">
              {sizesForColor.map((v) => (
                <button key={`${v.color}-${v.size}`} onClick={() => setSize(v.size)} disabled={v.quantity <= 0} data-testid={`storefront-variant-size-${String(v.size).toLowerCase()}`}
                  className={`h-11 min-w-[44px] px-3 rounded-lg text-sm border disabled:opacity-30 disabled:line-through ${size === v.size ? "text-white border-transparent" : "border-black/15 text-neutral-700 hover:border-black/40"}`}
                  style={size === v.size ? { background: acc } : {}}>{v.size}</button>
              ))}
            </div>
          </div>

          {selectedVariant && <div className="mt-3 text-sm text-neutral-500">{selectedVariant.quantity > 0 ? `${selectedVariant.quantity} en stock` : "Rupture de stock"}</div>}

          <Button onClick={add} className="mt-8 w-full sm:w-auto h-12 px-8 text-white font-semibold" style={{ background: acc }} data-testid="product-add-to-cart">
            <ShoppingBag size={18} className="mr-2" /> Ajouter au panier
          </Button>
        </div>
      </div>
    </div>
  );
}
